import { useShallow } from "zustand/react/shallow";

import { useOptionalGroupRegistry } from "../groups/GroupRegistry.context";
import {
  useWorkspace,
  useWorkspaceStore,
} from "../workspace/Workspace.context";
import type { GroupId, SymbolGroup } from "../workspace/workspace.types";
import { useGroupSelectController } from "./GroupSelect.controller";
import { deriveGroupSelectMembership } from "./GroupSelect.derive";
import { GroupSelectPresentation } from "./GroupSelect.pres";

export interface GroupSelectProps {
  widgetId: string;
}

/**
 * Wires the workspace store + global group registry to `GroupSelectPresentation`.
 *
 * All existing global groups are listed (including zero local members).
 * Create / delete / name / color go through the registry when mounted;
 * assign / unassign stay on the active workspace store.
 */
export const GroupSelect = ({ widgetId }: GroupSelectProps) => {
  const activeGroupId = useWorkspace(
    (s): GroupId | null => s.widgets[widgetId]?.groupId ?? null,
  );

  const groups = useWorkspace(
    useShallow((s): SymbolGroup[] => Object.values(s.groups)),
  );

  const memberCounts = useWorkspace(
    useShallow((s) => deriveGroupSelectMembership(s.widgets).memberCounts),
  );

  const linkedWidgetsByGroup = useWorkspace(
    useShallow(
      (s) => deriveGroupSelectMembership(s.widgets).linkedWidgetsByGroup,
    ),
  );

  const store = useWorkspaceStore();
  const registry = useOptionalGroupRegistry();
  const controller = useGroupSelectController({
    widgetId,
    groups,
    activeGroupId,
    memberCounts,
    linkedWidgetsByGroup,
    store,
    registry,
  });

  return <GroupSelectPresentation {...controller} />;
};
